import { useState } from 'react';
import { useThreatIntelFeeds, useThreatIntelStats, lookupIOC, reportIOC } from '../../hooks/useThreatIntel';

const IOC_TYPE_COLORS: Record<string, string> = {
  ip: 'bg-blue-100 text-blue-700',
  domain: 'bg-purple-100 text-purple-700',
  url: 'bg-indigo-100 text-indigo-700',
  sha256: 'bg-gray-100 text-gray-600',
  sha1: 'bg-gray-100 text-gray-600',
  md5: 'bg-gray-100 text-gray-600',
  email: 'bg-yellow-100 text-yellow-700',
  cidr: 'bg-red-100 text-red-700',
};

export default function ThreatIntelPage() {
  const { data: feedsData, loading: feedsLoading } = useThreatIntelFeeds();
  const { data: statsData, loading: statsLoading, refresh } = useThreatIntelStats();
  const [searchInput, setSearchInput] = useState('');
  const [lookupResult, setLookupResult] = useState<Record<string, unknown> | null>(null);
  const [lookupLoading, setLookupLoading] = useState(false);
  const [lookupError, setLookupError] = useState<string | null>(null);
  const [showReport, setShowReport] = useState(false);
  const [reportForm, setReportForm] = useState({ indicator: '', type: 'ip', context: '', risk_score: 50 });
  const [reporting, setReporting] = useState(false);

  const feeds = feedsData?.data ?? [];
  const stats = (statsData?.data ?? {}) as Record<string, unknown>;

  async function handleLookup() {
    if (!searchInput.trim()) return;
    setLookupLoading(true);
    setLookupError(null);
    setLookupResult(null);
    try {
      const result = await lookupIOC(searchInput.trim());
      setLookupResult(result.data);
    } catch (err) {
      setLookupError(err instanceof Error ? err.message : 'Lookup failed');
    } finally {
      setLookupLoading(false);
    }
  }

  async function handleReport() {
    if (!reportForm.indicator.trim()) return;
    setReporting(true);
    try {
      await reportIOC(reportForm as unknown as Record<string, unknown>);
      setShowReport(false);
      setReportForm({ indicator: '', type: 'ip', context: '', risk_score: 50 });
      refresh();
    } catch { /* handled */ } finally {
      setReporting(false);
    }
  }

  if (feedsLoading && statsLoading) {
    return (
      <div className="px-4 md:px-8 py-6 max-w-7xl mx-auto">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-gray-200 rounded w-64" />
          <div className="grid grid-cols-4 gap-4">{[1,2,3,4].map(i => <div key={i} className="h-24 bg-gray-200 rounded" />)}</div>
          <div className="h-64 bg-gray-200 rounded" />
        </div>
      </div>
    );
  }

  return (
    <div className="px-4 md:px-8 py-6 max-w-7xl mx-auto space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900">Threat Intelligence</h1>
        <button onClick={() => setShowReport(true)} className="px-4 py-2 text-sm bg-indigo-600 text-white rounded-md hover:bg-indigo-700">
          Report IOC
        </button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[
          { label: 'Total IOCs', value: String(stats['total_iocs'] ?? 0) },
          { label: 'Recent Lookups', value: String(stats['recent_lookups'] ?? 0) },
          { label: 'High Risk IOCs', value: String(stats['high_risk'] ?? 0) },
          { label: 'Active Feeds', value: String(feeds.length) },
        ].map(s => (
          <div key={s.label} className="bg-white rounded-lg shadow p-4">
            <p className="text-xs text-gray-500 uppercase">{s.label}</p>
            <p className="text-2xl font-bold text-gray-900 mt-1">{s.value}</p>
          </div>
        ))}
      </div>

      {/* IOC Lookup */}
      <div className="bg-white rounded-lg shadow p-6">
        <h2 className="text-base font-semibold text-gray-900 mb-4">IOC Lookup</h2>
        <div className="flex gap-3">
          <input
            value={searchInput}
            onChange={e => setSearchInput(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && handleLookup()}
            placeholder="Enter IP, domain, hash, URL, or email..."
            className="flex-1 border border-gray-300 rounded-md px-3 py-2 text-sm"
          />
          <button onClick={handleLookup} disabled={lookupLoading} className="px-4 py-2 text-sm bg-indigo-600 text-white rounded-md hover:bg-indigo-700 disabled:opacity-50">
            {lookupLoading ? 'Looking up...' : 'Lookup'}
          </button>
        </div>
        {lookupError && <p className="text-sm text-red-600 mt-2">{lookupError}</p>}
        {lookupResult && (
          <div className="mt-4 border border-gray-200 rounded-md p-4 space-y-2">
            <div className="flex items-center gap-3">
              <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${IOC_TYPE_COLORS[String(lookupResult['type'] ?? '')] ?? 'bg-gray-100 text-gray-600'}`}>
                {String(lookupResult['type'] ?? 'unknown')}
              </span>
              <span className="text-sm font-mono text-gray-900">{String(lookupResult['indicator'] ?? searchInput)}</span>
              <span className={`ml-auto text-sm font-bold ${Number(lookupResult['risk_score'] ?? 0) >= 70 ? 'text-red-600' : Number(lookupResult['risk_score'] ?? 0) >= 40 ? 'text-yellow-600' : 'text-green-600'}`}>
                Risk: {String(lookupResult['risk_score'] ?? 'N/A')}
              </span>
            </div>
            {!!lookupResult['reputation'] && <p className="text-sm text-gray-600">Reputation: {String(lookupResult['reputation'])}</p>}
            {!!lookupResult['tags'] && Array.isArray(lookupResult['tags']) && (
              <div className="flex gap-1 flex-wrap">
                {(lookupResult['tags'] as string[]).map((tag, i) => (
                  <span key={i} className="bg-gray-100 text-gray-600 text-xs px-2 py-0.5 rounded">{tag}</span>
                ))}
              </div>
            )}
            {!!lookupResult['context'] && <p className="text-sm text-gray-500">{String(lookupResult['context'])}</p>}
          </div>
        )}
      </div>

      {/* Threat Feeds */}
      <div className="bg-white rounded-lg shadow p-6">
        <h2 className="text-base font-semibold text-gray-900 mb-4">Threat Feeds ({feeds.length})</h2>
        {feeds.length === 0 ? (
          <p className="text-sm text-gray-400">No threat feeds configured.</p>
        ) : (
          <div className="space-y-3">
            {feeds.map((feed, i) => {
              const f = feed as Record<string, unknown>;
              return (
                <div key={i} className="flex items-center justify-between border-b border-gray-100 pb-3 last:border-0">
                  <div>
                    <p className="text-sm font-medium text-gray-900">{String(f['name'] ?? 'Unknown Feed')}</p>
                    <p className="text-xs text-gray-500">{String(f['provider'] ?? '')} — {String(f['ioc_count'] ?? 0)} IOCs</p>
                  </div>
                  <span className={`text-xs px-2 py-0.5 rounded-full ${f['enabled'] ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                    {f['enabled'] ? 'Active' : 'Disabled'}
                  </span>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Report IOC Modal */}
      {showReport && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setShowReport(false)}>
          <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md" onClick={e => e.stopPropagation()}>
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Report IOC</h3>
            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Indicator</label>
                <input value={reportForm.indicator} onChange={e => setReportForm({ ...reportForm, indicator: e.target.value })}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm" placeholder="IP, domain, hash..." />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
                <select value={reportForm.type} onChange={e => setReportForm({ ...reportForm, type: e.target.value })}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm">
                  {['ip', 'domain', 'url', 'md5', 'sha1', 'sha256', 'email', 'cidr'].map(t => <option key={t} value={t}>{t.toUpperCase()}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Context</label>
                <textarea value={reportForm.context} onChange={e => setReportForm({ ...reportForm, context: e.target.value })}
                  rows={3} className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm" placeholder="Where was this observed?" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Risk Score (0-100)</label>
                <input type="number" min={0} max={100} value={reportForm.risk_score}
                  onChange={e => setReportForm({ ...reportForm, risk_score: parseInt(e.target.value) || 0 })}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm" />
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-6">
              <button onClick={() => setShowReport(false)} className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800">Cancel</button>
              <button onClick={handleReport} disabled={reporting}
                className="px-4 py-2 text-sm bg-indigo-600 text-white rounded-md hover:bg-indigo-700 disabled:opacity-50">
                {reporting ? 'Reporting...' : 'Report IOC'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
